import type { GtfsAsDict, GtfsFormatted, GtfsFromApi } from '@/@types/gtfs-object';
import Common from './common';
import type { Stop, StopTime, Trip } from '@/@types/gtfs-ressource';

interface GtfsKeyFields {
  [key: string]: { id: string };
}

const _gtfsFields: GtfsKeyFields = {
  calendar_dates: { id: 'service_id' },
  calendars: { id: 'service_id' },
  routes: { id: 'route_id' },
  shapes: { id: 'shape_id' },
  stops: { id: 'stop_id' },
  trips: { id: 'trip_id' },
  stop_times: { id: 'trip_id' },
};

const _gtfsIndexedDBKeys: Array<keyof GtfsAsDict> = [
  'calendar_dates',
  'calendars',
  'routes',
  'shapes',
  'stops',
  'trips',
];

/**
 * Check if every data from indexedDB object are valid
 */
function isDataFromIndexedDBValid(gtfsData: GtfsAsDict) {
  try {
    // Check if gtfsData is an object
    if (!Common.isObject(gtfsData)) throw new Error('Undefined gtfsRessource');

    // Check if every key are in object
    _gtfsIndexedDBKeys.forEach(key => {
      if (!gtfsData?.[key]) throw new Error(`Undefined ${key}`);

      // Check if calendar_dates is an array
      if (key === 'calendar_dates' && !Array.isArray(gtfsData[key]))
        throw new Error(`${key} is not an Array but should be`);

      // check if every ressource except calendar_dates is an object
      if (key !== 'calendar_dates') {
        if (!Common.isObject(gtfsData[key])) throw new Error(`${key} is not an object but should be`);
      }

      // Check if every ressources have data in their object (except calendar_dates & calendars since they are not always mendatory)
      if (key !== 'calendar_dates' && key !== 'calendars') {
        if (Common.isEmptyObject(gtfsData[key])) throw new Error(`Empty object ${key}`);
      }
    });
  } catch (e) {
    if (e instanceof Error) {
      throw new Error(`Error from indexedDB fetched data : ${e.message}. Reloading GTFS from API`);
    }
  }
}

/** Convert an array to a dict based on  a gtfs key field */
function arrayToDict<T>(array: T[], keyField: keyof GtfsKeyFields): { [key: string]: T } {
  if (array.length === 0) return {};
  const tableDesc: keyof T = (_gtfsFields[keyField]?.id || 'id') as keyof T;

  const dictionary: { [key: string]: T } = {};
  for (const item of array) {
    dictionary[item[tableDesc] as any] = item;
  }
  return dictionary;
}

/** Format gtfs data from API to dictionnary*/
function formatFromApiData(gtfsApiResult: GtfsFromApi): GtfsAsDict {
  const gtfsFormated: GtfsAsDict = {
    calendar_dates: gtfsApiResult.calendar_dates || [],
    calendars: arrayToDict(gtfsApiResult.calendars || [], 'calendars'),
    routes: arrayToDict(gtfsApiResult.routes || [], 'routes'),
    shapes: arrayToDict(gtfsApiResult.shapes || [], 'shapes'),
    stops: arrayToDict(gtfsApiResult.stops || [], 'stops'),
    trips: arrayToDict(gtfsApiResult.trips || [], 'trips'),
  };
  return gtfsFormated;
}

/**
 * Filter gtfs based on private routes & format data
 */
function filterAndFormatGtfs(gtfsDict: GtfsAsDict, privateRoutes: Array<string>): Partial<GtfsFormatted> {
  const gtfsObject: Partial<GtfsFormatted> = {};
  // Filter gtfs because some routes are set to private ?
  const hasPrivateRoutes = privateRoutes?.length > 0;

  // calendars
  gtfsObject.calendars = gtfsDict.calendars;
  // calendar_dates
  gtfsObject.calendarDates = gtfsDict.calendar_dates || [];

  // routes
  const privateRouteIds = new Set(privateRoutes);
  if (hasPrivateRoutes && gtfsDict.routes) {
    // Remove private routes if necessary
    for (const [key] of Object.entries(gtfsDict.routes)) {
      if (privateRouteIds.has(key)) {
        delete gtfsDict.routes[key];
      }
    }
  }
  gtfsObject.routes = gtfsDict.routes;

  const shapes = gtfsDict.shapes || {};
  const trips: { [key: string]: Trip } = {};
  const routeStops: { [key: string]: { [key: string]: boolean } } = {};
  const terminus: { [key: string]: boolean } = {};

  // Filter trips if necessary
  if (hasPrivateRoutes && gtfsDict.trips) {
    // Remove trips linked to private routes if necessary
    for (const [key] of Object.entries(gtfsDict.trips)) {
      if (privateRouteIds.has(gtfsDict.trips[key].route_id)) {
        delete gtfsDict.trips[key];
      }
    }
  }

  // create used Shapes & stops list to filter the privates ones later: sets, as they
  // are looked up for every stop time of the network
  const usedShapes = new Set<string>();
  const usedStops = new Set<string>();
  for (const tripId in gtfsDict.trips) {
    const trip = gtfsDict.trips[tripId] as Trip;

    // if private routes, populate used shapes
    if (hasPrivateRoutes) usedShapes.add(trip.shape_id);

    trips[tripId] = trip;

    if (trip.shape_id && shapes[trip.shape_id]) {
      const shape = shapes[trip.shape_id];
      shape.routeId = shape.routeId || trip.route_id;
      shape.routeIds = shape.routeIds || [];
      if (!shape.routeIds.includes(trip.route_id)) {
        shape.routeIds.push(trip.route_id);
      }
    }

    const routeId = trip.route_id;
    if (!routeStops[routeId]) {
      routeStops[routeId] = {};
    }
    gtfsDict.trips[tripId].stop_times.forEach((st: StopTime) => {
      routeStops[routeId][st.stop_id] = true;
      // if private routes, populate used routes
      if (hasPrivateRoutes && !usedStops.has(st.stop_id)) {
        usedStops.add(st.stop_id);
        const parentStation = gtfsDict.stops?.[st.stop_id].parent_station;
        if (parentStation) usedStops.add(parentStation);
      }
    });

    if (gtfsDict.trips[tripId].stop_times.length > 0) {
      const sts = gtfsDict.trips[tripId].stop_times;
      const sids = [sts[0].stop_id, sts[sts.length - 1].stop_id];
      sids.forEach(sid => {
        const stop = gtfsDict.stops?.[sid];
        if (stop) {
          if (stop.parent_station) terminus[stop.parent_station] = true;
          else terminus[stop.stop_id] = true;
        }
      });
    }
  }
  // Remove unused shapes & stops if private routes
  if (hasPrivateRoutes && gtfsDict.stops) {
    for (const [key] of Object.entries(shapes)) {
      if (!usedShapes.has(key)) {
        delete shapes[key];
      }
    }
    for (const [key] of Object.entries(gtfsDict.stops)) {
      if (!usedStops.has(key)) {
        delete gtfsDict.stops[key];
      }
    }
  }

  gtfsObject.stops = gtfsDict.stops;
  gtfsObject.shapes = shapes;
  gtfsObject.trips = trips;
  gtfsObject.routeStops = routeStops;
  gtfsObject.terminus = terminus;

  return gtfsObject;
}

/**
 * The trip covering the widest portion of a route's itinerary, the one its drawn
 * shape follows: the longest of today's trips, or of `fallbackTrips` on a route
 * with no service today.
 */
function representativeTrip(tripsOfDay: Array<Trip>, fallbackTrips: Array<Trip> = []): Trip | undefined {
  const candidateTrips = tripsOfDay.length > 0 ? tripsOfDay : fallbackTrips;
  return candidateTrips.reduce<Trip | undefined>(
    (longest, trip) => (!longest || trip.stop_times.length > longest.stop_times.length ? trip : longest),
    undefined,
  );
}

/**
 * Stops of a route, by name. Stops are collapsed onto their canonical stop
 * (`canonicalOf`): the quays of a station, and the opposite direction on a
 * network without stations, are listed once.
 */
function listRouteStopsByName({
  routeStops,
  stops,
  canonicalOf,
}: {
  routeStops: { [stopId: string]: boolean };
  stops: { [stopId: string]: Stop };
  canonicalOf: { [stopId: string]: string };
}): Array<Stop> {
  const displayedIds = new Set<string>();
  Object.keys(routeStops).forEach(stopId => {
    const displayedId = canonicalOf[stopId] ?? stopId;
    if (stops[displayedId]) displayedIds.add(displayedId);
  });

  return [...displayedIds]
    .map(stopId => stops[stopId])
    .sort((a, b) => a.stop_name.localeCompare(b.stop_name));
}

export default {
  formatFromApiData,
  filterAndFormatGtfs,
  isDataFromIndexedDBValid,
  listRouteStopsByName,
  representativeTrip,
};
